<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div class="d-flex align-items-center">
          <button @click="back" class="btn btn-outline-secondary btn-icon-text py-2 px-3 mr-3">
            <i class="mdi mdi-arrow-left mr-1"></i> {{ $t('common.back') }}
          </button>
          <div>
            <h2 class="text-white font-weight-bold mb-1">{{ $t('approvalDetail.rewardApprovalTitle') }}</h2>
            <p class="text-muted small mb-0">{{ $t('approvalDetail.submissionSubtitle') }}</p>
          </div>
        </div>
        <div class="mt-2 mt-md-0">
          <span class="badge px-3 py-2 font-weight-bold" :class="statusBadgeClass">
            {{ statusText }}
          </span>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="col-lg-7 grid-margin">
      <!-- Quest & Player Details Card -->
      <div class="card mb-4">
        <div class="card-body">
          <div class="d-flex align-items-center justify-content-between pb-3 mb-3 border-bottom border-secondary">
            <div class="d-flex align-items-center">
              <div
                class="rounded-circle mr-3 d-flex align-items-center justify-content-center bg-dark text-info font-weight-bold"
                style="width: 44px; height: 44px; border: 1px solid rgba(0, 144, 231, 0.3);"
              >
                {{ (data.user && data.user.name ? data.user.name.charAt(0) : 'P').toUpperCase() }}
              </div>
              <div>
                <h5 class="text-white font-weight-bold mb-0">{{ data.user ? data.user.name : '-' }}</h5>
                <small class="text-muted">{{ data.user ? data.user.email : '-' }}</small>
              </div>
            </div>
            <div class="text-right">
              <span class="text-muted small d-block">{{ $t('approvalDetail.submittedAt') }}</span>
              <strong class="text-white small">{{ dateFormat(data.created_at) }}</strong>
            </div>
          </div>

          <div class="row g-3">
            <div class="col-md-6 mb-3">
              <label class="text-muted small text-uppercase font-weight-bold d-block">{{ $t('approvalDetail.questRule') }}</label>
              <div class="text-white font-weight-bold h6 mb-0">{{ data.rule ? data.rule.name : '-' }}</div>
            </div>
            <div class="col-md-6 mb-3">
              <label class="text-muted small text-uppercase font-weight-bold d-block">{{ $t('approvalDetail.rewardGold') }}</label>
              <div class="badge badge-warning px-2 py-1 font-weight-bold">
                <i class="mdi mdi-circle-multiple mr-1"></i>
                {{ data.rule && data.rule.non_gold ? data.rule.non_gold : ('+' + (data.rule ? data.rule.gold : 0) + ' Gold') }}
              </div>
            </div>
            <div class="col-12 mb-3" v-if="data.rule && data.rule.description">
              <label class="text-muted small text-uppercase font-weight-bold d-block">{{ $t('approvalDetail.description') }}</label>
              <p class="text-white-50 small mb-0 bg-dark p-3 rounded border border-secondary">
                {{ data.rule.description }}
              </p>
            </div>
            <div class="col-12 mb-3" v-if="data.comment">
              <label class="text-muted small text-uppercase font-weight-bold d-block">{{ $t('approvalDetail.comment') }}</label>
              <div class="p-3 bg-dark rounded border border-secondary text-white small">
                <i class="mdi mdi-comment-text-outline text-info mr-1"></i>
                "{{ data.comment }}"
              </div>
            </div>
            <div class="col-12 mb-3" v-if="data.external_link">
              <label class="text-muted small text-uppercase font-weight-bold d-block">{{ $currentLocale === 'id' ? 'Tautan Eksternal' : 'External Link' }}</label>
              <div class="p-3 bg-dark rounded border border-secondary small">
                <a :href="data.external_link" target="_blank" rel="noopener noreferrer" class="text-info font-weight-bold">
                  <i class="mdi mdi-link-variant mr-1"></i>{{ data.external_link }}
                </a>
              </div>
            </div>
            <div class="col-12 mb-3" v-if="data.status === 'rejected' && data.rejection_notes">
              <label class="text-muted small text-uppercase font-weight-bold d-block text-danger">{{ $t('approvalDetail.rejectionNotes') }}</label>
              <div class="p-3 bg-danger-subtle rounded border border-danger text-danger-light small">
                <i class="mdi mdi-alert-circle mr-1"></i>
                {{ data.rejection_notes }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Evidence Attachment Card -->
      <div class="card">
        <div class="card-header bg-transparent border-bottom border-secondary pb-3">
          <h5 class="text-white font-weight-bold mb-0">
            <i class="mdi mdi-image-area text-primary mr-2"></i>{{ $t('approvalDetail.evidenceAttachment') }}
          </h5>
        </div>
        <div class="card-body">
          <div v-if="evidenceUrl" class="text-center">
            <div class="position-relative d-inline-block border border-secondary rounded overflow-hidden" style="max-height: 400px; max-width: 100%;">
              <img
                :src="evidenceUrl"
                alt="Proof of work"
                class="img-fluid rounded"
                style="max-height: 400px; object-fit: contain; cursor: pointer;"
                @click="showModal = true"
              />
            </div>
            <div class="mt-3">
              <button type="button" class="btn btn-outline-info btn-sm" @click="showModal = true">
                <i class="mdi mdi-magnify-plus-outline mr-1"></i> {{ $t('approvalDetail.viewAttachment') }}
              </button>
            </div>
          </div>
          <div v-else class="text-center py-5 text-muted">
            <i class="mdi mdi-image-off-outline display-4 mb-2 d-block"></i>
            <p class="mb-0">{{ $t('approvalDetail.noAttachment') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column: Approval Decision -->
    <div class="col-lg-5 grid-margin">
      <div class="card">
        <div class="card-header bg-transparent border-bottom border-secondary pb-3">
          <h5 class="text-white font-weight-bold mb-0">
            <i class="mdi mdi-shield-check text-warning mr-2"></i>{{ $t('approvalDetail.decisionAction') }}
          </h5>
        </div>
        <div class="card-body">
          <form v-if="data.status === 'pending'" @submit.prevent="submitForm">
            <div class="form-group mb-4">
              <label class="text-muted small text-uppercase font-weight-bold mb-2 d-block">{{ $t('common.status') }}</label>
              <div class="d-flex gap-2">
                <button
                  type="button"
                  class="btn flex-fill py-2 font-weight-bold"
                  :class="status === 'approved' ? 'btn-success' : 'btn-outline-secondary'"
                  @click="status = 'approved'"
                >
                  <i class="mdi mdi-check-circle mr-1"></i> {{ $t('approvalDetail.approve') }}
                </button>
                <button
                  type="button"
                  class="btn flex-fill py-2 font-weight-bold"
                  :class="status === 'rejected' ? 'btn-danger' : 'btn-outline-secondary'"
                  @click="status = 'rejected'"
                >
                  <i class="mdi mdi-close-circle mr-1"></i> {{ $t('approvalDetail.reject') }}
                </button>
              </div>
            </div>

            <div class="form-group mb-4" v-if="status === 'rejected'">
              <label class="text-muted small text-uppercase font-weight-bold mb-2 d-block">
                {{ $t('approvalDetail.rejectionNotes') }} <span class="text-danger">*</span>
              </label>
              <textarea
                class="form-control"
                rows="4"
                v-model="rejection_notes"
                :placeholder="$t('approvalDetail.reasonPlaceholder')"
                required
              ></textarea>
            </div>

            <button
              type="submit"
              class="btn btn-primary w-100 py-3 font-weight-bold"
              :disabled="loading || (status === 'rejected' && !rejection_notes.trim())"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm mr-2" role="status"></span>
              <i v-else class="mdi mdi-check-all mr-1"></i>
              {{ $t('approvalDetail.submitDecision') }}
            </button>
          </form>

          <div v-else class="text-center py-4">
            <div class="mb-3">
              <i
                class="mdi display-4"
                :class="data.status === 'approved' ? 'mdi-check-decagram text-success' : 'mdi-close-octagon text-danger'"
              ></i>
            </div>
            <h5 class="text-white font-weight-bold mb-1">
              {{ data.status === 'approved' ? $t('common.approved') : $t('common.rejected') }}
            </h5>
            <p class="text-muted small mb-0">
              {{ data.status === 'approved' ? $t('approvalDetail.rewardApprovedMsg') : $t('approvalDetail.rewardRejectedMsg') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Lightbox Modal -->
    <div
      v-if="showModal"
      class="modal-backdrop-custom d-flex align-items-center justify-content-center"
      @click.self="showModal = false"
    >
      <div class="modal-card bg-dark border border-secondary rounded p-3 position-relative" style="max-width: 90vw; max-height: 90vh;">
        <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom border-secondary">
          <h6 class="text-white font-weight-bold mb-0">{{ $currentLocale === 'id' ? 'Pratinjau Bukti Klaim' : 'Proof Preview' }}</h6>
          <div>
            <a :href="evidenceUrl" target="_blank" rel="noopener noreferrer" class="btn btn-xs btn-outline-info mr-2">
              <i class="mdi mdi-open-in-new mr-1"></i> {{ $currentLocale === 'id' ? 'Buka Ukuran Penuh' : 'Open Full Size' }}
            </a>
            <button type="button" class="btn btn-xs btn-outline-light" @click="showModal = false">
              <i class="mdi mdi-close mr-1"></i> {{ $t('common.close') }}
            </button>
          </div>
        </div>
        <div class="text-center overflow-auto" style="max-height: 80vh;">
          <img :src="evidenceUrl" alt="Proof of work enlarged" class="img-fluid rounded" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDetailRewardApproval, updateRewardApproval } from '@/services/approvalService';
import Swal from 'sweetalert2';
import moment from 'moment';
import config from '@/config/config';

export default {
  name: 'EditRewardApproval',
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    status: 'approved',
    data: {},
    rejection_notes: '',
    showModal: false,
    config,
  }),
  computed: {
    evidenceUrl() {
      const ev = this.data?.evidence || this.data?.evidence_path;
      if (!ev) return null;
      return ev.startsWith('http') ? ev : `${this.config.STORAGE_URL}${ev}`;
    },
    statusBadgeClass() {
      if (this.data.status === 'approved') return 'badge-success';
      if (this.data.status === 'rejected') return 'badge-danger';
      return 'badge-warning';
    },
    statusText() {
      if (this.data.status === 'approved') return this.$t('common.approved');
      if (this.data.status === 'rejected') return this.$t('common.rejected');
      return this.$t('common.pending');
    },
  },
  watch: {
    status() {
      if (this.status !== 'rejected') {
        this.rejection_notes = '';
      }
    },
  },
  methods: {
    dateFormat(date) {
      if (!date) return '-';
      return moment(date).format('DD MMM YYYY, HH:mm');
    },
    async fetchData() {
      try {
        const res = await getDetailRewardApproval(this.id);
        this.data = res.data;
      } catch (error) {
        console.error('Error fetching detail reward approval:', error);
      }
    },
    back() {
      this.$router.go(-1);
    },
    async submitForm() {
      this.loading = true;
      try {
        await updateRewardApproval(this.id, this.status, this.rejection_notes);
        await Swal.fire({
          title: this.$t('common.success'),
          text: this.$t('approvalDetail.submitDecision'),
          icon: 'success',
        });
        this.back();
      } catch (error) {
        console.error('Error submitting approval:', error);
        Swal.fire({
          title: this.$t('common.error'),
          text: this.$currentLocale === 'id' ? 'Gagal mengirim keputusan verifikasi.' : 'Failed to submit decision.',
          icon: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 1050;
}
.bg-danger-subtle {
  background-color: rgba(239, 68, 68, 0.1);
}
.text-danger-light {
  color: #fca5a5;
}
</style>
